import { formatYmd } from '../utils/wallClock';

export const cityPlanningEn = {
  back: 'Back to trip',
  pageTitle: 'AI City Plan',
  untitledCity: 'City',
  loading: 'Loading plan…',
  findingPlan: 'Finding your AI plan…',
  findingPlanSubtitle: 'This may take a moment',
  queued: 'Generating your plan…',
  running: 'Still working on suggestions…',
  failed: 'We could not generate a plan for this city.',
  failedRetry: 'Try again',
  generatePlan: 'Generate Plan',
  generatePlanBody: 'Generate an AI-powered itinerary for {city}',
  missingTrip: 'Trip not found.',
  missingCity: 'Choose a city from your trip to review its AI plan.',
  noSubscription: 'AI planning requires an active subscription.',
  noPlanYet: 'No plan generated yet',
  noPlanYetBody: 'Generate a plan below, or wait if a job is already running.',
  noPlanForDay: 'No plan for this day',
  readOnly: 'You can view this plan, but only editors can add items to the trip.',
  footerDaily: 'Daily',
  footerHotels: 'Hotels',
  footerTours: 'Tours',
  searchPlaces: 'Search places',
  cityInfo: 'City Info',
  cityDescriptionFallback: 'A beautiful destination waiting to be explored.',
  duration: 'Duration',
  durationDay: 'day',
  durationDays: 'days',
  highlights: 'City Highlights',
  highlightsEmpty: 'No highlights available yet',
  itineraryPlan: 'Itinerary Plan',
  tourSearchPlaceholder: 'Search tours (e.g. cooking class)',
  tourSearchAction: 'Search',
  tourSearchSearching: 'Searching tours…',
  tourSearchOffline: 'Connect to the internet to search tours.',
  tourSearchError: "Couldn't search tours",
  tourSearchEmpty: 'No tours matched that search.',
  tourSearchLocalHint: 'Showing local matches — press Search for more results.',
  toursForYou: 'For You',
  toursViator: 'Viator',
  toursEmpty: 'No tours available for this city yet.',
  mustSee: 'Must see',
  emptySlot: 'No suggestions for this slot.',
  emptyItinerary: 'No day-by-day itinerary in this plan yet.',
  addToTrip: 'Add to trip',
  addAsOptional: 'Add as Option',
  bookTour: 'Book on Viator',
  saveDay: 'Save Day',
  daySaved: 'Day added to your trip.',
  daySavedSummary: 'Added {saved} item(s). {conflicts} skipped due to conflicts.',
  daySavedSimple: 'Added {saved} item(s).',
  itemAdded: 'Added to your trip.',
  optionAdded: 'Added to Things I May Do.',
  overlapWarning: 'This overlaps something already on your schedule.',
  pickDate: 'Date',
  pickTime: 'Start time',
  cancel: 'Cancel',
  confirmAdd: 'Add',
  confirmationNumber: 'Confirmation number',
  confirmationNumberHint: 'Optional — add now or skip if you do not have it yet.',
  aiStatusQueued: 'Planning…',
  aiStatusRunning: 'Planning…',
  aiStatusDone: 'Plan ready',
  aiStatusFailed: 'Plan failed',
  viewerCannotSave: 'Viewers cannot edit this trip.',
  tourSource: 'Viator',
  planSource: 'AI suggestion',
  scheduled: 'Scheduled',
  slotOf: '{current} of {total}',
  hotelsTitle: 'Hotels',
  hotelsEmpty: 'No hotel suggestions in this plan yet.',
  stayArea: 'Recommended stay area',
  stayAreaRationale: 'Why this area',
  hotelFrom: 'From {price}',
  hotelAdd: 'Add hotel note',
  expand: 'Expand',
  collapse: 'Collapse',
  /** @deprecated tabs removed — kept for older tests */
  tabSlots: 'Daily slots',
  tabItinerary: 'Itinerary',
  tabTours: 'Tours',
} as const;

export function aiStatusLabel(status: string | null | undefined): string | null {
  const s = String(status ?? '')
    .trim()
    .toLowerCase();
  if (!s) return null;
  if (s === 'queued' || s === 'pending') return cityPlanningEn.aiStatusQueued;
  if (s === 'running' || s === 'processing') return cityPlanningEn.aiStatusRunning;
  if (s === 'done' || s === 'complete' || s === 'completed') {
    return cityPlanningEn.aiStatusDone;
  }
  if (s === 'failed' || s === 'error') return cityPlanningEn.aiStatusFailed;
  return null;
}

export function formatDayChip(ymd: string): string {
  // UTC wall-clock civil date — same digits iOS shows (not device-local midnight).
  return formatYmd(ymd, { weekday: 'short', month: 'short', day: 'numeric' });
}

export function recommendedTimeBadge(raw: string): string {
  const t = raw.trim().toLowerCase();
  if (t.startsWith('morn') || t === 'morning') return 'Morn';
  if (t.startsWith('aft') || t === 'afternoon') return 'Aft';
  if (t.startsWith('eve') || t === 'evening') return 'Eve';
  if (t.startsWith('break')) return 'Brk';
  if (t.startsWith('lunch')) return 'Lunch';
  if (t.startsWith('din')) return 'Din';
  if (t.startsWith('night')) return 'Night';
  return raw.slice(0, 4);
}
